import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "design_system/libs"

import "./index.css"

// The reading page. Both variants share this container ("uses the same container"); only the corner
// rounding and the spine treatment differ.
const pageVariants = cva(
  "flex flex-col h-full will-change-transform border border-secondary-200 dark:border-secondary-700 min-h-[225px] p-4 bg-secondary-100 font-serif text-lg",
  {
    variants: {
      variant: {
        // Closed book: page edges stacked at the top, one rounded corner (bottom-left).
        closed: "rounded-bl-[5px]",
        // Open book: the inside page, rounded on the left where it meets the spine.
        open: "openBookPage rounded-l-[6px]",
      },
    },
    defaultVariants: { variant: "closed" },
  }
)

interface AdventureStoryPreviewProps extends VariantProps<typeof pageVariants> {
  title?: React.ReactNode
  className?: string
  /** Show the page-stack "cover" (closed variant only). When false it is hidden but keeps its space,
   *  so toggling it never shifts the body — no jitter when navigating. Defaults to true. */
  cover?: boolean
  /** Widen the cover so the page edges line up with the page and only a slim back-cover sliver shows
   *  on the right (an "opened to a page" look). Off is the narrower "closed book" look. The cover's
   *  height is unchanged either way, so toggling `spread` never shifts the body. */
  spread?: boolean
  /** Scroll the content inside the page instead of the whole component: the page frame and cover stay
   *  put while only the body scrolls. The parent must give it a bounded height (e.g. a flex column
   *  with a max-height), since the page then fills that height and scrolls internally. */
  scrollBody?: boolean
}

export const AdventureStoryPreview = ({
  title,
  className,
  variant = "closed",
  cover = true,
  spread = false,
  scrollBody = false,
}: AdventureStoryPreviewProps) => (
  <div
    className={cn(
      "relative",
      variant === "open" && "openBook",
      scrollBody && "flex min-h-0 flex-1 flex-col",
    )}
  >
    {variant === "open" ? (
      // Open book: the left cover / already-read pages curl up toward the spine and fade out.
      <div className="openBookSpine" aria-hidden />
    ) : (
      // Closed book: fake page edges stacked at the top. `invisible` (not hidden) keeps the space so
      // the body stays put when the cover is toggled off; `spread` only changes its width.
      <div
        className={cn("storyPages", spread && "storyPagesSpread", !cover && "invisible")}
        aria-hidden={!cover}
      >
        <div className="storyPagesContainer">
          <hr />
          <hr />
          <hr />
        </div>
      </div>
    )}
    <div
      className={cn(
        pageVariants({ variant }),
        // `relative z-10` sits the page (the centered modal content) above the absolute back cover.
        scrollBody && "relative z-10 min-h-0 flex-1",
        className,
      )}
    >
      {scrollBody ? (
        // Only the content scrolls; the page frame + cover stay fixed.
        <div className="min-h-0 flex-1 overflow-y-auto">{title}</div>
      ) : (
        title
      )}
    </div>
  </div>
)

export default AdventureStoryPreview
